import React, { useEffect, useState, useCallback } from 'react';
import {
  X,
  Loader2,
  User,
  Building2,
  Mail,
  Phone,
  Car,
  MessageCircle,
  CreditCard,
  CalendarRange,
  LifeBuoy,
} from 'lucide-react';
import bookingService from '../../../services/bookingService';

const formatCurrency = (amount, currency = 'MAD') => {
  if (amount == null || Number.isNaN(Number(amount))) return 'N/D';
  return new Intl.NumberFormat('fr-FR', {
    style: 'currency',
    currency: currency || 'MAD',
  }).format(Number(amount));
};

const formatDateTime = (iso) => {
  if (!iso) return 'N/D';
  return new Date(iso).toLocaleString('fr-FR', {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

/**
 * Détail réservation admin (DTO GET /admin/bookings/:id) — chat lecture seule.
 */
const BookingAdminDetail = ({ bookingId, onClose, onActionComplete }) => {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const load = useCallback(async () => {
    if (!bookingId) return;
    setLoading(true);
    setError(null);
    try {
      const res = await bookingService.getBookingById(bookingId);
      if (res?.success && res.data) {
        setDetail(res.data);
      } else {
        setError('Réponse invalide du serveur');
      }
    } catch (e) {
      setError(e.message || 'Impossible de charger la réservation');
    } finally {
      setLoading(false);
    }
  }, [bookingId]);

  useEffect(() => {
    load();
  }, [load]);

  const handleCancelBooking = async () => {
    if (!detail?.id) return;
    const reason = window.prompt('Motif d’annulation (optionnel) :') ?? '';
    if (!window.confirm('Annuler cette réservation ?')) return;
    setActionLoading(true);
    try {
      await bookingService.updateBookingStatus(detail.id, 'CANCELLED', reason || 'Annulation administrative');
      await load();
      onActionComplete?.();
    } catch (e) {
      alert(e.message || 'Erreur lors de l’annulation');
    } finally {
      setActionLoading(false);
    }
  };

  const handleValidatePayment = async () => {
    if (!detail?.id) return;
    if (!window.confirm('Marquer le paiement comme payé (statut financier PAID) ?')) return;
    setActionLoading(true);
    try {
      await bookingService.updatePaymentStatus(detail.id, 'PAID');
      await load();
      onActionComplete?.();
    } catch (e) {
      alert(e.message || 'Erreur lors de la mise à jour du paiement');
    } finally {
      setActionLoading(false);
    }
  };

  if (!bookingId) return null;

  return (
    <div className="flex flex-col h-full min-h-0 bg-white">
      <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200 shrink-0">
        <div>
          <h2 className="text-lg font-bold text-gray-900">Détail réservation</h2>
          <p className="text-xs text-gray-500 font-mono mt-0.5">{bookingId}</p>
        </div>
        <button
          type="button"
          onClick={onClose}
          className="p-2 rounded-lg hover:bg-gray-100 text-gray-600"
          aria-label="Fermer"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      <div className="flex-1 overflow-y-auto px-5 py-4 space-y-6">
        {loading && (
          <div className="flex justify-center py-16">
            <Loader2 className="w-10 h-10 animate-spin text-[#27489E]" />
          </div>
        )}

        {error && !loading && (
          <div className="rounded-xl bg-red-50 border border-red-200 text-red-800 text-sm p-4">{error}</div>
        )}

        {!loading && detail && (
          <>
            {/* Header actions */}
            <div className="flex flex-wrap items-center gap-2">
              <span
                className={`inline-flex px-3 py-1 rounded-full text-xs font-bold ${
                  detail.status === 'CANCELLED' || detail.status === 'DECLINED'
                    ? 'bg-red-100 text-red-800'
                    : detail.status === 'COMPLETED'
                      ? 'bg-green-100 text-green-800'
                      : detail.status === 'LIVE'
                        ? 'bg-blue-100 text-blue-800'
                        : 'bg-gray-100 text-gray-800'
                }`}
              >
                {detail.status || 'N/D'}
              </span>
              <span className="text-xs text-gray-500">
                Paiement résa : {detail.paymentStatus ?? 'N/D'}
              </span>
            </div>

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                disabled={
                  actionLoading || !['PENDING', 'CONFIRMED', 'LIVE'].includes(detail.status || '')
                }
                onClick={handleCancelBooking}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-red-50 text-red-700 border border-red-200 hover:bg-red-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Annuler manuellement
              </button>
              <button
                type="button"
                disabled={actionLoading || detail.finance?.financialStatus === 'PAID'}
                onClick={handleValidatePayment}
                className="px-3 py-2 text-sm font-semibold rounded-lg bg-emerald-50 text-emerald-800 border border-emerald-200 hover:bg-emerald-100 disabled:opacity-40 disabled:cursor-not-allowed"
              >
                Valider le paiement
              </button>
              <a
                href="mailto:support@carvy.ma?subject=Support%20réservation%20admin"
                className="inline-flex items-center gap-1.5 px-3 py-2 text-sm font-semibold rounded-lg bg-gray-50 text-gray-800 border border-gray-200 hover:bg-gray-100"
              >
                <LifeBuoy className="w-4 h-4" />
                Contacter le support
              </a>
            </div>

            {/* Finance */}
            <section>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
                <CreditCard className="w-4 h-4 text-[#27489E]" />
                Finance
              </h3>
              <div className="rounded-xl border border-gray-200 overflow-hidden text-sm">
                <table className="w-full">
                  <tbody className="divide-y divide-gray-100">
                    <tr className="bg-gray-50/80">
                      <td className="px-4 py-2.5 text-gray-600">Montant brut (total)</td>
                      <td className="px-4 py-2.5 font-semibold text-right">
                        {formatCurrency(detail.finance?.grossAmount, detail.finance?.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 text-gray-600">Commission Carvy</td>
                      <td className="px-4 py-2.5 font-semibold text-right">
                        {formatCurrency(detail.finance?.commissionCarvy, detail.finance?.currency)}
                      </td>
                    </tr>
                    <tr className="bg-gray-50/80">
                      <td className="px-4 py-2.5 text-gray-600">Net vendeur</td>
                      <td className="px-4 py-2.5 font-semibold text-right text-green-700">
                        {formatCurrency(detail.finance?.vendorNet, detail.finance?.currency)}
                      </td>
                    </tr>
                    <tr>
                      <td className="px-4 py-2.5 text-gray-600">Statut paiement (financier)</td>
                      <td className="px-4 py-2.5 font-semibold text-right">
                        {detail.finance?.financialStatus ?? 'N/D'}
                      </td>
                    </tr>
                    <tr className="bg-gray-50/80">
                      <td className="px-4 py-2.5 text-gray-600">Méthode</td>
                      <td className="px-4 py-2.5 text-right">{detail.payment_method_name ?? 'N/D'}</td>
                    </tr>
                  </tbody>
                </table>
              </div>
              <p className="text-xs text-gray-500 mt-2">
                Caution (snapshot) : {detail.logistics?.deposit != null ? detail.logistics.deposit : 'N/D'} · KM
                prévu :{' '}
                {detail.logistics?.plannedMileageKm != null ? detail.logistics.plannedMileageKm : 'N/D'}
              </p>
            </section>

            {/* Parties */}
            <section className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div className="rounded-xl border border-gray-200 p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <User className="w-4 h-4" />
                  Client
                </h3>
                {detail.parties?.client ? (
                  <ul className="text-sm space-y-1.5 text-gray-800">
                    <li className="font-semibold">
                      {detail.parties.client.fullName ||
                        [detail.parties.client.firstName, detail.parties.client.lastName].filter(Boolean).join(' ') ||
                        'N/D'}
                    </li>
                    <li className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      {detail.parties.client.email ?? 'N/D'}
                    </li>
                    <li className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-3.5 h-3.5 shrink-0" />
                      {detail.parties.client.phoneNumber ?? 'N/D'}
                    </li>
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">N/D</p>
                )}
              </div>
              <div className="rounded-xl border border-gray-200 p-4">
                <h3 className="text-xs font-bold text-gray-500 uppercase tracking-wide mb-3 flex items-center gap-2">
                  <Building2 className="w-4 h-4" />
                  Vendeur / Agence
                </h3>
                {detail.parties?.vendor ? (
                  <ul className="text-sm space-y-1.5 text-gray-800">
                    <li className="font-semibold">
                      {detail.parties.vendor.companyName ||
                        detail.parties.vendor.fullName ||
                        [detail.parties.vendor.firstName, detail.parties.vendor.lastName].filter(Boolean).join(' ') ||
                        'N/D'}
                    </li>
                    <li className="flex items-center gap-2 text-gray-600">
                      <Mail className="w-3.5 h-3.5 shrink-0" />
                      {detail.parties.vendor.email ?? 'N/D'}
                    </li>
                    <li className="flex items-center gap-2 text-gray-600">
                      <Phone className="w-3.5 h-3.5 shrink-0" />
                      {detail.parties.vendor.phoneNumber ?? 'N/D'}
                    </li>
                  </ul>
                ) : (
                  <p className="text-sm text-gray-500">N/D</p>
                )}
              </div>
            </section>

            {/* Logistique / véhicule */}
            <section>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
                <Car className="w-4 h-4 text-[#27489E]" />
                Véhicule & dates
              </h3>
              <div className="rounded-xl border border-gray-200 p-4 space-y-3 text-sm">
                <div className="flex items-start gap-2">
                  <CalendarRange className="w-4 h-4 text-gray-500 mt-0.5 shrink-0" />
                  <div>
                    <p className="text-gray-600">Début → Fin</p>
                    <p className="font-medium text-gray-900">
                      {formatDateTime(detail.logistics?.dates?.start)} →{' '}
                      {formatDateTime(detail.logistics?.dates?.end)}
                    </p>
                  </div>
                </div>
                <div>
                  <p className="text-gray-600 text-xs mb-1">Véhicule</p>
                  <p className="font-semibold text-gray-900">{detail.vehicle?.title ?? 'N/D'}</p>
                  <p className="text-gray-700">
                    Immatriculation :{' '}
                    <span className="font-mono">{detail.vehicle?.licencePlate ?? 'N/D'}</span>
                  </p>
                </div>
                {(detail.vehicle?.mainImage || detail.vehicle?.snapshotImage) && (
                  <img
                    src={detail.vehicle.mainImage || detail.vehicle.snapshotImage}
                    alt=""
                    className="w-full max-h-40 object-cover rounded-lg border border-gray-100"
                  />
                )}
                {Array.isArray(detail.logistics?.interiorImages) && detail.logistics.interiorImages.length > 0 && (
                  <div>
                    <p className="text-xs text-gray-600 mb-2">Photos intérieures (réservation)</p>
                    <div className="flex flex-wrap gap-2">
                      {detail.logistics.interiorImages.map((url, i) => (
                        <img key={i} src={url} alt="" className="h-16 w-16 object-cover rounded border" />
                      ))}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Chat read-only */}
            <section>
              <h3 className="text-sm font-bold text-gray-900 flex items-center gap-2 mb-3">
                <MessageCircle className="w-4 h-4 text-[#27489E]" />
                Chat (lecture seule)
                {detail.conversation?.id && (
                  <span className="text-xs font-normal text-gray-500 font-mono">#{detail.conversation.id}</span>
                )}
              </h3>
              {!detail.conversation?.id ? (
                <p className="text-sm text-gray-500 rounded-xl border border-dashed border-gray-200 p-4">
                  Aucune conversation liée à cette réservation.
                </p>
              ) : (
                <div className="rounded-xl border border-gray-200 bg-gray-50 p-3 max-h-72 overflow-y-auto space-y-2">
                  {detail.messages?.length === 0 && (
                    <p className="text-sm text-gray-500 text-center py-4">Aucun message.</p>
                  )}
                  {detail.messages?.map((msg) => {
                    const isClient = msg.senderRole === 'client';
                    const isVendor = msg.senderRole === 'vendor';
                    return (
                      <div
                        key={msg.id}
                        className={`flex ${isVendor ? 'justify-end' : 'justify-start'}`}
                      >
                        <div
                          className={`max-w-[85%] rounded-2xl px-3 py-2 text-sm shadow-sm ${
                            isVendor
                              ? 'bg-[#27489E] text-white rounded-br-md'
                              : 'bg-gray-200 text-gray-900 rounded-bl-md'
                          } ${!isClient && !isVendor ? 'bg-amber-100 text-amber-900' : ''}`}
                        >
                          <p className="text-[10px] opacity-80 mb-1">
                            {isClient ? 'Client' : isVendor ? 'Vendeur' : 'Autre'} ·{' '}
                            {formatDateTime(msg.createdAt)}
                          </p>
                          <p className="whitespace-pre-wrap break-words">{msg.text}</p>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </section>

            {detail.otp && (
              <section className="text-xs text-gray-500 border-t pt-4">
                OTP retrait / retour : {detail.otp.pickup ?? '—'} / {detail.otp.drop ?? '—'}
              </section>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BookingAdminDetail;
